import React, { useEffect, useRef } from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import * as THREE from "three";
import { COLORS, FONTS, LOGO } from "./theme";
import bgConfig from "./bg_config.json";
import { Background } from "./Background";

export const AnaliseCorporal3D: React.FC<{

  headline: string;
  subheadline: string;
  ctaText: string;
}> = ({ headline, subheadline, ctaText }) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const objectsRef = useRef<{
    core: THREE.Mesh;
    ring1: THREE.Mesh;
    ring2: THREE.Mesh;
    nodes: THREE.Mesh[];
    particles: THREE.Points;
  } | null>(null);

  const isVertical = height > width;

  // 1. Initialize Three.js on Mount
  useEffect(() => {
    if (bgConfig.type !== "none") return;
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    
    // Background matching brand dark bg, but dark transparent for CSS blending
    scene.background = new THREE.Color("#131110");
    scene.fog = new THREE.FogExp2("#131110", 0.08);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    // Position camera closer or further based on layout orientation
    camera.position.set(0, 0, isVertical ? 10 : 7.5);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(1);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.4;

    // Ambient Lighting
    const ambientLight = new THREE.AmbientLight("#ffffff", 0.25);
    scene.add(ambientLight);

    // Directional Lighting for shine and shadows
    const dirLight1 = new THREE.DirectionalLight("#ffffff", 1.8);
    dirLight1.position.set(5, 10, 7);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(COLORS.gold, 1.2);
    dirLight2.position.set(-5, -5, -2);
    scene.add(dirLight2);

    // Point Light in core
    const pointLight = new THREE.PointLight(COLORS.gold, 5, 15);
    pointLight.position.set(0, 0, 0);
    scene.add(pointLight);

    // --- Create 3D Objects ---

    // 1. Core Sphere (Self/Mind)
    const coreGeo = new THREE.SphereGeometry(1.0, 64, 64);
    const coreMat = new THREE.MeshStandardMaterial({
      color: COLORS.gold,
      roughness: 0.15,
      metalness: 0.85,
      emissive: COLORS.gold,
      emissiveIntensity: 0.25,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    scene.add(core);

    // 2. Connection Rings (represent systemic flow)
    const ring1Geo = new THREE.TorusGeometry(2.3, 0.015, 16, 120);
    const ringMat1 = new THREE.MeshBasicMaterial({
      color: COLORS.gold,
      transparent: true,
      opacity: 0.25,
    });
    const ring1 = new THREE.Mesh(ring1Geo, ringMat1);
    ring1.rotation.x = Math.PI / 2.2;
    scene.add(ring1);

    const ring2Geo = new THREE.TorusGeometry(2.3, 0.012, 16, 120);
    const ringMat2 = new THREE.MeshBasicMaterial({
      color: COLORS.gold,
      transparent: true,
      opacity: 0.18,
    });
    const ring2 = new THREE.Mesh(ring2Geo, ringMat2);
    ring2.rotation.x = Math.PI / 3.5;
    ring2.rotation.y = Math.PI / 4.5;
    scene.add(ring2);

    // 3. The 5 Orbiting Trait Nodes
    // Colors corresponding to standard traits colors (Esquizoide, Oral, Psicopata, Masoquista, Rígido)
    const nodeColors = [
      "#3b82f6", // Blue: Esquizoide (Mental/Creative)
      "#f59e0b", // Amber/Gold: Oral (Feeling/Communication)
      "#e11d48", // Rose/Crimson: Psicopata (Leadership/Strategy)
      "#78716c", // Stone/Taupe: Masoquista (Resilience/Execution)
      "#10b981", // Emerald: Rígido (Agility/Detail)
    ];

    const nodeGeo = new THREE.SphereGeometry(0.24, 32, 32);
    const nodes: THREE.Mesh[] = [];

    for (let i = 0; i < 5; i++) {
      const nodeMat = new THREE.MeshStandardMaterial({
        color: nodeColors[i],
        roughness: 0.1,
        metalness: 0.7,
        emissive: nodeColors[i],
        emissiveIntensity: 0.4,
      });
      const node = new THREE.Mesh(nodeGeo, nodeMat);
      scene.add(node);
      nodes.push(node);
    }

    // 4. Subtle Golden Floating Star Dust
    const particleGeo = new THREE.BufferGeometry();
    const pCount = 300;
    const posArray = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 12;
    }
    particleGeo.setAttribute("position", new THREE.BufferAttribute(posArray, 3));
    const particleMat = new THREE.PointsMaterial({
      color: COLORS.gold,
      size: 0.035,
      transparent: true,
      opacity: 0.5,
    });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    // Store references
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    objectsRef.current = { core, ring1, ring2, nodes, particles };

    // Cleanup
    return () => {
      coreGeo.dispose();
      coreMat.dispose();
      ring1Geo.dispose();
      ringMat1.dispose();
      ring2Geo.dispose();
      ringMat2.dispose();
      nodeGeo.dispose();
      particleGeo.dispose();
      particleMat.dispose();
      renderer.dispose();
    };
  }, [width, height, isVertical]);

  // 2. Render Frame Deterministically on Remotion Frame Updates
  useEffect(() => {
    if (bgConfig.type !== "none") return;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const objects = objectsRef.current;

    if (!scene || !camera || !renderer || !objects) return;

    // Standard continuous rotation logic mapped directly to frame
    objects.core.rotation.y = frame * 0.012;
    objects.core.rotation.x = frame * 0.007;

    objects.ring1.rotation.z = frame * 0.004;
    objects.ring2.rotation.z = -frame * 0.006;

    // Orbiting nodes logic
    const radius = 2.3;
    for (let i = 0; i < 5; i++) {
      // Offset starting angle by 72deg (2*PI / 5) for even spacing
      const angle = (i * 2 * Math.PI) / 5 + frame * 0.008;
      objects.nodes[i].position.x = Math.cos(angle) * radius;
      objects.nodes[i].position.z = Math.sin(angle) * radius;
      // Soft wave on Y-axis
      objects.nodes[i].position.y = Math.sin(frame * 0.015 + i) * 0.15;
    }

    // Slowly rotate environment dust particles
    objects.particles.rotation.y = frame * 0.001;
    objects.particles.rotation.x = frame * 0.0005;

    // Micro-pan camera angle to create depth of field motion
    camera.position.x = Math.sin(frame * 0.004) * 0.8;
    camera.position.y = Math.cos(frame * 0.004) * 0.5;
    camera.lookAt(0, 0, 0);

    renderer.render(scene, camera);
  }, [frame]);

  // --- Spring Animations for UI overlay ---
  const sprConfig = { stiffness: 80, damping: 14 };
  
  // Left panel slide in
  const panelX = spring({
    frame,
    fps,
    config: sprConfig,
    from: -600,
    to: 0,
  });

  const contentOpacity = spring({
    frame: frame - 10,
    fps,
    config: sprConfig,
    from: 0,
    to: 1,
  });

  return (
    <AbsoluteFill
      style={{
        background: "#131110",
        fontFamily: FONTS.sans,
        overflow: "hidden",
      }}
    >
      <Background theme="dark" />

      {/* 3D Canvas container - shifted right to make space for layout content */}
      {bgConfig.type === "none" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: isVertical ? 0 : 260,
            right: 0,
            bottom: 0,
            zIndex: 1,
            opacity: isVertical ? 0.65 : 1,
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
            }}
          />
        </div>
      )}

      {/* Subtle radial overlay gradient to blend the 3D scene smoothly */}
      {bgConfig.type === "none" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: isVertical
              ? "radial-gradient(circle at center, rgba(19,17,16,0.3) 0%, rgba(19,17,16,0.95) 90%)"
              : "linear-gradient(90deg, rgba(19,17,16,1) 0%, rgba(19,17,16,0.9) 25%, rgba(19,17,16,0.4) 60%, rgba(19,17,16,0) 100%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Premium UI Overlay Panel */}
      <div
        style={{
          position: "absolute",
          left: isVertical ? 0 : 70,
          top: isVertical ? "auto" : 0,
          bottom: 0,
          width: isVertical ? "100%" : 560,
          height: isVertical ? "auto" : "100%",
          padding: isVertical ? "40px 50px 80px" : "80px 0px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          zIndex: 3,
          transform: isVertical ? "none" : `translateX(${panelX}px)`,
          opacity: isVertical ? contentOpacity : 1,
        }}
      >
        {/* Double-Bezel nested container for absolute high-end aesthetic */}
        <div
          style={{
            background: "rgba(25, 23, 22, 0.75)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            padding: isVertical ? "40px" : "50px",
            borderRadius: 32,
            boxShadow: "0 24px 60px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.06)",
          }}
        >
          {/* Tag Category */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 14px",
              background: "rgba(197, 160, 89, 0.08)",
              border: `1px solid rgba(197, 160, 89, 0.25)`,
              borderRadius: 9999,
              color: COLORS.gold,
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              marginBottom: 30,
            }}
          >
            <span style={{ fontSize: 12 }}>✨</span> Mapamento 3D de Caráter
          </div>

          {/* Logo Brand Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <div style={LOGO}>E</div>
            <span
              style={{
                fontSize: 13,
                color: COLORS.stoneSubtle,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              Edson Santoro
            </span>
          </div>

          {/* Headline */}
          <h1
            style={{
              fontFamily: FONTS.serif,
              fontSize: isVertical ? 48 : 56,
              fontWeight: 400,
              color: COLORS.white,
              lineHeight: 1.1,
              margin: 0,
              marginBottom: 20,
              letterSpacing: "-0.02em",
            }}
          >
            {headline}
          </h1>

          {/* Subheadline description */}
          <p
            style={{
              fontSize: isVertical ? 16 : 18,
              color: COLORS.stoneSubtle,
              lineHeight: 1.6,
              margin: 0,
              marginBottom: 32,
              fontWeight: 300,
            }}
          >
            {subheadline}
          </p>

          {/* Staggered traits list matching orbiting colors */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 36,
              borderTop: "1px solid rgba(255, 255, 255, 0.08)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              padding: "24px 0",
            }}
          >
            {[
              { name: "Esquizoide", res: "Criatividade & Pensamento Fora da Caixa", color: "#3b82f6" },
              { name: "Oral", res: "Comunicação & Inteligência Emocional", color: "#f59e0b" },
              { name: "Psicopata", res: "Liderança & Articulação de Negócios", color: "#e11d48" },
              { name: "Masoquista", res: "Resiliência & Segurança de Execução", color: "#78716c" },
              { name: "Rígido", res: "Foco em Perfeição & Competitividade", color: "#10b981" },
            ].map((t, idx) => {
              const slide = spring({
                frame: frame - (15 + idx * 4),
                fps,
                config: sprConfig,
                from: 20,
                to: 0,
              });
              const opacity = spring({
                frame: frame - (15 + idx * 4),
                fps,
                config: sprConfig,
                from: 0,
                to: 1,
              });

              return (
                <div
                  key={t.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    transform: `translateX(${slide}px)`,
                    opacity,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: t.color,
                      boxShadow: `0 0 10px ${t.color}`,
                    }}
                  />
                  <div style={{ fontSize: 13, color: COLORS.white, fontWeight: 600, width: 80 }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.stoneMuted, fontWeight: 300 }}>
                    {t.res}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            {/* Clock Estimate */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: COLORS.stoneSubtle,
                fontSize: 12,
              }}
            >
              <span style={{ fontSize: 16 }}>⏱️</span>
              <span>Sessão de 1h30</span>
            </div>

            {/* CTA Button */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 28px",
                background: COLORS.gold,
                color: "#131110",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                borderRadius: 9999,
                boxShadow: `0 8px 24px ${COLORS.gold}30`,
              }}
            >
              {ctaText} <span>→</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating tag bottom-right */}
      {!isVertical && (
        <div
          style={{
            position: "absolute",
            right: 70,
            bottom: 60,
            fontSize: 11,
            color: COLORS.stoneMuted,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            zIndex: 3,
            background: "rgba(19, 17, 16, 0.4)",
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid rgba(255, 255, 255, 0.05)",
          }}
        >
          @santoro.analista
        </div>
      )}
    </AbsoluteFill>
  );
};
